// ============================================================
// <App />
//
// Top of the React tree. Wires:
//   - ThemeProvider (from @square/ui) consuming the active vertical
//   - AuthProvider  (local) for auth state
//   - BrowserRouter (react-router-dom) for routing
//   - OliviumErrorBoundary as a top-level fallback
//
// Sentry is initialized in main.tsx; if it's installed AND a DSN
// is set, the Sentry React error boundary wraps everything one
// level deeper. Both wrappings are safe — Sentry's boundary just
// reports the error and re-throws to ours.
// ============================================================

import { useEffect, useState, type ReactNode } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { ThemeProvider } from '@square/ui'
import { OliviumErrorBoundary } from '@square/olivium/console'
import type { VerticalConfig, FrontendEnv } from '@square/core'

import { AuthProvider } from './auth/AuthContext'
import AppRoutes from './router'

interface AppProps {
  vertical: VerticalConfig
  env: FrontendEnv
}

export default function App({ vertical, env }: AppProps) {
  return (
    <SentryBoundary>
      <OliviumErrorBoundary scope="app">
        <ThemeProvider vertical={vertical}>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes vertical={vertical} env={env} />
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </OliviumErrorBoundary>
    </SentryBoundary>
  )
}

/**
 * SentryBoundary — lazy wrapper. If @sentry/react is installed
 * AND VITE_SENTRY_DSN is set, mounts Sentry.ErrorBoundary;
 * otherwise renders children directly. Lets the build succeed
 * even when @sentry/react is absent (it's an optionalDependency).
 */
function SentryBoundary({ children }: { children: ReactNode }) {
  const [Wrapper, setWrapper] = useState<((props: { children: ReactNode }) => JSX.Element) | null>(null)

  useEffect(() => {
    if (!import.meta.env.VITE_SENTRY_DSN) return
    let cancelled = false
    import('@sentry/react')
      .then((Sentry) => {
        if (cancelled) return
        const Boundary = Sentry.ErrorBoundary
        setWrapper(() => (props: { children: ReactNode }) => (
          <Boundary fallback={<SentryFallback />}>{props.children}</Boundary>
        ))
      })
      .catch(() => {
        // Sentry not installed — that's fine.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!Wrapper) return <>{children}</>
  return <Wrapper>{children}</Wrapper>
}

function SentryFallback() {
  return (
    <div
      style={{
        padding: 40,
        fontFamily: 'var(--font-mono)',
        color: 'var(--olv-red)',
        fontSize: 12,
      }}
    >
      Square Code stopped rendering. The error has been reported.
    </div>
  )
}
